import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { ChartConfig }                         from '../chart';
import { ChartHelper }                         from '../chart-helper';

interface FunnelStage {
	x: number;
	y: number;
	width: number;
	height: number;
	fill: string;
	label: string;
	/** Truncated stage name drawn in the left gutter. */
	displayLabel: string;
	/** Full stage name (hover <title> when truncated). */
	fullLabel: string;
	/** Compact value drawn inside the bar ('' when the bar is too narrow). */
	displayValue: string;
	/** Conversion vs the first stage, drawn right of the bar (e.g. '42%'). */
	displayPercent: string;
	/** Tooltip body: exact value + share of first + share of previous stage. */
	text: string;
}

/**
 * Funnel chart (Power BI style): one horizontal bar per stage, centered, widths
 * proportional to value — read top-to-bottom as a narrowing flow. The right gutter
 * shows each stage's conversion vs the FIRST stage; tooltips add the step-by-step
 * conversion vs the previous stage. One dataset; values should be positive and
 * (typically) descending, but neither is enforced — the data is drawn as given.
 */
@Component({
	standalone:  false,
	selector:    '[cs-funnel-chart]',
	templateUrl: './funnel-chart.component.html'
})
export class CsFunnelChartComponent implements OnInit, OnChanges {

	@Input() config: ChartConfig;

	stages: FunnelStage[] = [];
	labelGutter = 110;
	percentGutter = 52;

	private isDataReady() {
		return this.config?.data?.datasets?.length && this.config.data.datasets[0].data?.length;
	}

	ngOnInit() {
		setTimeout(() => {
			if (this.isDataReady()) { this.calculate(); }
		}, 1);
	}

	ngOnChanges(changes: any) {
		if (changes.hasOwnProperty('config') && this.isDataReady()) {
			this.calculate();
		}
	}

	private calculate() {
		const w = this.config.chartWidth  - this.config.xPadding * 2;
		const h = this.config.chartHeight - this.config.yPadding * 2;
		if (!w || !h || w < 10 || h < 10) { return; }

		const values = this.config.data.datasets[0].data.map(v => (Number.isFinite(v) && v > 0 ? v : 0));
		const labels = this.config.data.labels ?? [];
		const max    = Math.max(...values, 1);
		const first  = values.find(v => v > 0) ?? 1;

		const n         = values.length;
		const rowGap    = 3;
		const rowHeight = Math.min(46, (h - rowGap * (n - 1)) / n);
		// Vertically centered as a block when few stages leave spare height.
		const blockTop  = this.config.yPadding + Math.max(0, (h - (rowHeight + rowGap) * n + rowGap) / 2);
		const plotWidth = w - this.labelGutter - this.percentGutter;
		const centerX   = this.config.xPadding + this.labelGutter + plotWidth / 2;
		const maxChars  = Math.max(4, Math.floor((this.labelGutter - 12) / 6.5));

		this.stages = values.map((value, i) => {
			const width   = Math.max(value > 0 ? 2 : 0, (value / max) * plotWidth);
			const y       = blockTop + i * (rowHeight + rowGap);
			const full    = String(labels[i] ?? '');
			const ofFirst = Math.round(value / first * 1000) / 10;
			const prev    = i > 0 ? values[i - 1] : 0;
			const ofPrev  = prev > 0 ? Math.round(value / prev * 1000) / 10 : null;
			return {
				x:      centerX - width / 2,
				y,
				width,
				height: rowHeight,
				fill:   this.config.colors[i % this.config.colors.length],
				label:  full,
				displayLabel: full.length > maxChars ? full.slice(0, maxChars - 1).trimEnd() + '…' : full,
				fullLabel:    full,
				// Value inked inside only when it plausibly fits (~7px/char at font 11).
				displayValue: width >= String(ChartHelper.getValueFormatted(Math.round(value))).length * 7 + 12
					? ChartHelper.getValueFormatted(Math.round(value))
					: '',
				displayPercent: `${ofFirst}%`,
				text: `${ChartHelper.getValueLocale(value)}\n${ofFirst}% of first`
					+ (ofPrev !== null ? `\n${ofPrev}% of previous` : '')
			};
		});
	}
}
